import { XIcon } from '@commander/ui';
import { useUserDetails } from '@commander/user-details'; // Import icons
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingCompletion from '../components/OnboardingSteps/OnboardingCompletion';
import OnboardingWizard from '../components/OnboardingWizard';
import type { UserRole } from '../types/onboarding.types';
import { validateUserRole } from '../utils/helpers';

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { userDetails, isLoading, isError } = useUserDetails();
  const [userRole, setUserRole] = useState<UserRole | null>(null);

  // Determine user role when user data is available
  useEffect(() => {
    if (userDetails) {
      const role = validateUserRole(userDetails);
      setUserRole(role);
    }
  }, [userDetails]);

  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    // Redirect to dashboard after onboarding
    navigate('/dashboard');
  };

  // Handle onboarding exit
  const handleOnboardingExit = () => {
    // Could redirect to a different page or show a confirmation dialog
    navigate('/dashboard');
  };

  // Get initial step based on user's current progress
  const getInitialStep = () => {
    if (userDetails?.step_number) {
      // Convert from 1-based step number to 0-based index and clamp to valid range
      // step_number 9 means last step, so we convert to index 8
      const zeroBasedIndex = Math.max(0, userDetails.step_number - 1);
      return zeroBasedIndex;
    }
    return 0;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading onboarding...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XIcon className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Onboarding</h2>
          <p className="text-gray-600 mb-4">Failed to load user details</p>
          <div className="space-x-4">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-600/90 transition-colors"
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user has already completed onboarding
  if (userDetails?.onboarding_complete) {
    return <OnboardingCompletion />;
  }

  // Main onboarding wizard
  return (
    <div className="">
      <OnboardingWizard
        onComplete={handleOnboardingComplete}
        onExit={handleOnboardingExit}
        initialStep={getInitialStep()}
        role={userRole || undefined}
      />
    </div>
  );
};
export default OnboardingPage;
